/** One ledger entry — the page behind a row of the audit ledger (Batch 5).
 *
 * The list answers "what happened, newest first"; this answers "show me this one": what was recorded,
 * exactly what was hashed, which entry it links back to, and where each identifier in it leads.
 *
 * ## It reads the chain the way the list does, and claims no more
 *
 * There is no "entry by sequence" endpoint. `useChainEntry` asks `GET /ledger` for two rows ending at
 * the sequence, so the page holds the entry **and its predecessor**. That is what lets the link be
 * checked in the browser — this entry's `prev_hash` against the predecessor's `hash` — rather than
 * asserted. The server's `check` covers those two entries and no more, and the page says so: it is
 * **not** a verification of the chain, and a green tick for the whole chain beside one entry would be
 * the overclaim ADR-0020 exists to prevent. `make verify-ledger` is the whole-chain check.
 *
 * ## Rule 4 holds here too
 *
 * The chain holds a person as an identifier and never a name: the hash covers five things and a
 * person is not one. **The page may show a name where the reader is allowed one** (U7, KD 2026-11-03):
 * an Admin sees the person's name with the identifier on hover, as a join made at the reader's
 * request (`PersonRef`, the roster), and every other role sees the identifier. The entry's payload,
 * which is the thing an auditor re-performs, is printed below as the chain holds it.
 *
 * ## References are followed only where a page exists for them
 *
 * `ledgerLinks.ts` is the table: record ids go to `/records/{id}`, a calculation opens the receipt
 * drawer (rule 3's one rendering of a figure), a person goes to People for an Admin — and every other
 * identifier is shown and not linked.
 *
 * ## Append-only
 *
 * Nothing on this page writes. A correction is a new entry made by the thing being corrected.
 */

import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import type { ChainCheck, ChainEntry, ChainPage } from "../api/ledger";
import { useChainEntry } from "../api/ledger";
import { Refused } from "../api/refusal";
import { Loaded, ReadFailed } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import { actorKindWords, ledgerEventWords } from "../lib/customerWords";
import { instantDate } from "../lib/format";
import type { Reader } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { kindLabel } from "./kinds";
import { Integrity } from "./LedgerIntegrity";
import { referenceFor, subjectReference } from "./ledgerLinks";
import type { RefKind } from "./ledgerLinks";
import { ANCHOR, CHECK, asText, atTime } from "./ledgerWords";
import { PersonRef } from "./PersonRef";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import { BasisAndLimits, Facts } from "./ScreenParts";
import type { FactSpec } from "./ScreenParts";
import { useScreen } from "./useScreen";
import parts from "./ScreenParts.module.css";
import styles from "./Screen.module.css";
import rec from "./Record.module.css";
import led from "./Ledger.module.css";
import own from "./LedgerEntry.module.css";

/** Mirrored from `api/ledger.py`'s `MAY_READ`, as the list's is. */
const MAY_READ: readonly string[] = ["admin", "approver", "auditor"];

function cls(...names: Array<string | false | undefined>): string {
  return names.filter(Boolean).join(" ");
}

function Pair({ k, children }: { k: string; children: ReactNode }) {
  return (
    <>
      <dt className={rec.k}>{k}</dt>
      <dd className={rec.v}>{children}</dd>
    </>
  );
}

function BackToLedger() {
  return (
    <Link className={styles.fixLink} to="/ledger" search={keepSearch} data-testid="ledger-back">
      <ArrowLeft size={13} weight="bold" aria-hidden="true" /> Back to the Audit ledger
    </Link>
  );
}

/** An identifier, and — where the table says a page exists for it — the way to that page. */
function Ref({
  kind,
  value,
  onReceipt,
}: {
  kind: RefKind | null;
  value: string;
  onReceipt: (calculationId: string) => void;
}) {
  if (kind === "person") {
    return <PersonRef userId={value} showName />;
  }
  if (kind === "record") {
    return (
      <span className={own.ref}>
        <span className={own.id}>{value}</span>
        <Link
          className={styles.fixLink}
          to="/records/$recordId"
          params={{ recordId: value }}
          search={keepSearch}
          aria-label={`Open the record ${value}`}
          data-testid="ledger-ref-record"
          data-ref-id={value}
        >
          Open record ›
        </Link>
      </span>
    );
  }
  if (kind === "receipt") {
    return (
      <span className={own.ref}>
        <span className={own.id}>{value}</span>
        <button
          type="button"
          className={led.receipt}
          data-testid="ledger-ref-receipt"
          data-ref-id={value}
          onClick={() => onReceipt(value)}
        >
          Open this figure&rsquo;s receipt
        </button>
      </span>
    );
  }
  return <span className={own.id}>{value}</span>;
}

/** This entry's own commit state. **The chain-wide state says whether the head is committed; this
 * says whether *this* entry is covered by a commit**, which is a different and more useful fact
 * here, and is derived from the one number the check carries (`last_anchored_seq`). */
function commitOf(seq: number, check: ChainCheck): { tone: StatusTone; short: string; text: string } {
  if (check.anchor_state === "ahead_of_chain") {
    const state = ANCHOR.ahead_of_chain;
    return { tone: "crit", short: state?.short ?? "Past the chain", text: state?.detail(check) ?? "" };
  }
  if (check.last_anchored_seq === null) {
    return {
      tone: "warn",
      short: "Not committed",
      text: "Nothing in this chain has been committed anywhere that cannot be rewritten, so no commit covers this entry.",
    };
  }
  if (seq <= check.last_anchored_seq) {
    return {
      tone: "ok",
      short: "Covered",
      text: `A commit at #${check.last_anchored_seq} covers this entry: the head at that point was written to storage that cannot be rewritten.`,
    };
  }
  return {
    tone: check.anchor_state === "overdue" ? "warn" : "info",
    short: "After the last commit",
    text: `The last commit is at #${check.last_anchored_seq}. This entry is newer and is covered by the next one.`,
  };
}

function LinkCheck({ entry, predecessor }: { entry: ChainEntry; predecessor: ChainEntry | undefined }) {
  // **Three outcomes, and the third is not a pass.** A predecessor that matches is a link seen; one
  // that does not is a break; none returned (the first entry has nothing before it, anything else is
  // a page that did not carry it) is *not checked here*, which is not the same as intact.
  if (entry.seq === 1) {
    return (
      <p className={own.linkState} data-testid="ledger-entry-link" data-link="first">
        <StatusPill tone="neutral">First entry</StatusPill>
        <span>Nothing comes before it, so there is no earlier hash to match.</span>
      </p>
    );
  }
  if (!predecessor) {
    return (
      <p className={own.linkState} data-testid="ledger-entry-link" data-link="unchecked">
        <StatusPill tone="neutral">Not checked here</StatusPill>
        <span>The earlier entry was not returned with this one, so its hash was not compared.</span>
      </p>
    );
  }
  const matches = predecessor.hash === entry.prev_hash;
  return matches ? (
    <p className={own.linkState} data-testid="ledger-entry-link" data-link="matches">
      <StatusPill tone="ok">Matches #{predecessor.seq}</StatusPill>
      <span>
        The hash this entry links back to is the hash of #{predecessor.seq}, compared by this page
        from the two entries it holds.
      </span>
    </p>
  ) : (
    <p className={own.linkState} role="alert" data-testid="ledger-entry-link" data-link="mismatch">
      <StatusPill tone="crit">Does not match #{predecessor.seq}</StatusPill>
      <span>
        The hash this entry links back to is not the hash of #{predecessor.seq}. A chain cannot be
        like that and still verify, so treat the record as broken here.
      </span>
    </p>
  );
}

function EntryBody({
  entry,
  page,
  onReceipt,
}: {
  entry: ChainEntry;
  page: ChainPage;
  onReceipt: (calculationId: string) => void;
}) {
  const reader: Reader | undefined = useReader();
  const time = atTime(entry.recorded_at, reader);
  const predecessor = page.rows.find((row) => row.seq === entry.seq - 1);
  const check = page.check;
  const state = CHECK[check.state];
  const commit = commitOf(entry.seq, check);
  const keys = Object.keys(entry.payload);
  const subject = subjectReference(entry.object_type, entry.object_id);
  const newer = page.head_seq !== null && entry.seq < page.head_seq;

  const facts: FactSpec[] = [
    { key: "ledger-entry-seq", label: "Sequence", value: `#${entry.seq}`, note: page.head_seq === null ? undefined : `of ${page.head_seq} in the chain` },
    { key: "ledger-entry-type", label: "Type", value: ledgerEventWords(entry.type), textual: true, note: actorKindWords(entry.actor_kind) },
    {
      key: "ledger-entry-adds-up",
      label: "Does it add up",
      value: state?.short ?? "Unrecognised",
      textual: true,
      note: `Over this entry and the one before it`,
    },
    { key: "ledger-entry-committed", label: "Committed unalterably", value: commit.short, textual: true },
  ];

  return (
    <>
      <PageHeader
        subtitle={`${ledgerEventWords(entry.type)} · ${instantDate(entry.recorded_at, reader)}${time ? ` ${time}` : ""}`}
        actions={
          <span className={own.nav}>
            {entry.seq > 1 ? (
              <Link
                className={styles.fixLink}
                to="/ledger/$seq"
                params={{ seq: String(entry.seq - 1) }}
                search={keepSearch}
                data-testid="ledger-entry-older"
              >
                ‹ Older, #{entry.seq - 1}
              </Link>
            ) : null}
            {newer ? (
              <Link
                className={styles.fixLink}
                to="/ledger/$seq"
                params={{ seq: String(entry.seq + 1) }}
                search={keepSearch}
                data-testid="ledger-entry-newer"
              >
                Newer, #{entry.seq + 1} ›
              </Link>
            ) : null}
            <BackToLedger />
          </span>
        }
      />
      <Facts facts={facts} label="This entry at a glance" />

      <div className={parts.area}>
        <div className={parts.stack}>
          <Panel title="What was recorded" column="1 / 13">
            <dl className={cls(rec.kv, own.kv)} data-testid="ledger-entry-facts">
              <Pair k="Type">
                {/* Words first (U8). The exact type the chain hashed is the hover and `data-event-type`, and
                    is in the CSV, for the auditor who needs it. */}
                <span data-testid="ledger-entry-type-value" data-event-type={entry.type} title={entry.type}>
                  {ledgerEventWords(entry.type)}
                </span>
              </Pair>
              <Pair k="Recorded">
                {instantDate(entry.recorded_at, reader)}
                {time ? ` ${time}` : ""}
              </Pair>
              <Pair k="About">
                {kindLabel(entry.object_type)}
                <span className={own.aboutId}>
                  <Ref kind={subject} value={entry.object_id} onReceipt={onReceipt} />
                </span>
              </Pair>
              <Pair k="Recorded by">
                <span data-testid="ledger-entry-actor">
                  {actorKindWords(entry.actor_kind)}
                  {entry.actor_id ? (
                    <span className={own.aboutId}>
                      <Ref kind="person" value={entry.actor_id} onReceipt={onReceipt} />
                    </span>
                  ) : (
                    // The kind travels with the identifier because null is *the engine*, not *we
                    // did not record who* — two facts a blank cell would merge (design rule 5).
                    <span className={own.aboutId}> · automatic, no person</span>
                  )}
                </span>
              </Pair>
              {entry.calculation_id ? (
                <Pair k="Figure">
                  <Ref kind="receipt" value={entry.calculation_id} onReceipt={onReceipt} />
                </Pair>
              ) : null}
            </dl>
            <p className={styles.note} data-testid="ledger-entry-actor-note">
              A person appears here as an identifier and never as a name, so this record can be both
              unalterable and erasable. An Admin can follow an identifier to People.
            </p>
          </Panel>

          <Panel
            title="What was hashed"
            sub={`${keys.length} ${keys.length === 1 ? "key" : "keys"}`}
            column="1 / 13"
          >
            {keys.length === 0 ? (
              <p className={led.payloadNote} data-testid="ledger-entry-no-payload">
                This entry carries no payload that the product can show. Either it was written with
                none, or its entry type no longer declares the keys it holds.
              </p>
            ) : (
              <dl className={led.pairs} data-testid="ledger-entry-payload">
                {keys.map((key) => {
                  const value = entry.payload[key];
                  const kind = referenceFor(key, value);
                  return (
                    <div
                      key={key}
                      className={cls(led.pair, entry.prose_keys.includes(key) && led.prose)}
                      data-key={key}
                    >
                      <dt className={led.key}>{key}</dt>
                      <dd className={led.value}>
                        {kind ? (
                          <Ref kind={kind} value={value as string} onReceipt={onReceipt} />
                        ) : (
                          asText(value)
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}
            {entry.undeclared_keys.length > 0 ? (
              <p className={led.undeclared} data-testid="ledger-entry-undeclared">
                <strong>Not shown: {entry.undeclared_keys.join(", ")}.</strong> This entry type does
                not declare those keys, so their values are named and not printed. Every payload is
                typed and bounded when it is written, and that is the only reason the values above
                can be shown as they are.
              </p>
            ) : null}
            <p className={styles.note} data-testid="ledger-entry-covers">
              The hash is computed over {page.hash_covers.join(", ")}. The values above are shown
              exactly as stored, unrounded, because they are what was hashed.
            </p>
          </Panel>

          <Panel title="Its place in the chain" column="1 / 13">
            <dl className={cls(rec.kv, own.kv)} data-testid="ledger-entry-hashes">
              <Pair k="Hash">
                <span className={own.mono} data-testid="ledger-entry-hash">
                  {entry.hash}
                </span>
              </Pair>
              <Pair k="Links back to">
                <span className={own.mono} data-testid="ledger-entry-prev-hash">
                  {entry.prev_hash}
                </span>
              </Pair>
            </dl>
            <LinkCheck entry={entry} predecessor={predecessor} />
          </Panel>

          <Panel
            title="What the chain proves"
            sub="Over this entry and the one before it — not the whole chain"
            column="1 / 13"
          >
            <Integrity check={check} />
            <p className={cls(own.commit)} data-testid="ledger-entry-commit">
              <StatusPill tone={commit.tone}>{commit.short}</StatusPill>
              <span>{commit.text}</span>
            </p>
            <p className={styles.note} data-testid="ledger-entry-scope">
              The check above recomputed {check.entries_checked}{" "}
              {check.entries_checked === 1 ? "entry" : "entries"}, not the chain. The whole chain is
              checked by <code>make verify-ledger</code>, which re-verifies every hash and every
              commit.
            </p>
          </Panel>
        </div>
      </div>

      <div className={parts.area}>
        <BasisAndLimits testId="ledger-entry-basis">
          <p>
            An entry&rsquo;s hash covers {page.hash_covers.join(", ")}. Who recorded it, when, and
            what it was about are protected by the table being insert-only, not by the chain.
          </p>
          <p>
            A link to a record, a person or a receipt is a convenience for reading: it opens the
            current state of the thing the identifier names, which may since have changed. The entry
            itself never does.
          </p>
        </BasisAndLimits>
      </div>
    </>
  );
}

function LedgerEntryBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const { seq: raw } = useParams({ from: "/ledger/$seq" });
  //: Digits only. `Number("1e3")` and `Number("")` are numbers and neither is a sequence.
  const seq = /^[1-9]\d*$/.test(raw) ? Number(raw) : Number.NaN;
  const mayRead = me ? MAY_READ.includes(me.role) : undefined;
  const page = useChainEntry(seq, mayRead === true);
  const refusal = page.error instanceof Refused ? page.error : null;
  const engagement = refusal?.code === "chain_has_no_period" ? refusal : null;

  return (
    <ScreenFrame
      screen={screen}
      title={Number.isNaN(seq) ? "Ledger entry" : `Ledger entry #${seq}`}
      crumb="one recorded act, and what it links to"
      bodyScrolls
    >
      {Number.isNaN(seq) ? (
        <Panel title="Ledger entry" column="1 / 13">
          <p className={styles.note} data-testid="ledger-entry-bad-seq">
            That is not a sequence number. Entries are numbered from 1.
          </p>
          <BackToLedger />
        </Panel>
      ) : mayRead === false ? (
        <Panel title="Ledger entry" column="1 / 13">
          <p className={led.whoCan} data-testid="ledger-who-can">
            <strong>An Admin, an Approver or an Auditor reads the chain whole.</strong> Every figure
            you can see still resolves to its own ledger entry through its receipt.
          </p>
        </Panel>
      ) : engagement ? (
        <Panel title="Ledger entry" column="1 / 13">
          <p className={led.whoCan} data-testid="ledger-no-period">
            <strong>Your engagement covers a period, and the ledger has none to compare it to.</strong>{" "}
            {engagement.message}
          </p>
        </Panel>
      ) : page.error ? (
        <Panel title="Ledger entry" column="1 / 13">
          <ReadFailed of="This ledger entry" error={page.error} onRetry={() => void page.refetch()} />
        </Panel>
      ) : (
        <Loaded query={page} of="This ledger entry">
          {(data) => {
            const entry = data.rows.find((row) => row.seq === seq);
            return entry ? (
              <EntryBody entry={entry} page={data} onReceipt={screen.openReceipt} />
            ) : (
              <Panel title="Ledger entry" column="1 / 13">
                <p className={styles.note} data-testid="ledger-entry-absent">
                  There is no entry #{seq} in this chain
                  {data.head_seq === null ? ", which has no entries yet" : `: the newest is #${data.head_seq}`}.
                </p>
                <BackToLedger />
              </Panel>
            );
          }}
        </Loaded>
      )}
    </ScreenFrame>
  );
}

export function LedgerEntryScreen() {
  return (
    <RequireSession>
      <LedgerEntryBody />
    </RequireSession>
  );
}
